import React, { useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import { NamePath } from '@xbcb/ui-types';
import {
  StyledUnitAssistInputMinusButtonWrapperDiv,
  StyledUnitAssistInputContainer,
} from './styles';
import CurrencyField from '../CurrencyField';
import { LabelAbove } from '@xbcb/static-text-components';
import { MinusButton } from '@xbcb/button-components';
import PercentInput from 'components/PercentInput';
import { useTheme } from 'styled-components';

import { getEnv } from '@xbcb/ui-utils';

const { stage } = getEnv();

const lineLevelNamePathDepth = 4;

export interface UnitAssistInputProps {
  form: FormInstance;
  fullNamePath: NamePath;
  localNamePath: NamePath;
  max?: number;
  readOnly?: boolean;
  disabled?: boolean;
  step?: number;
  $itemSize?: string;
  precision?: number;
  required?: boolean;
  currency?: string;
  debounce?: boolean;
  currencyNamePath?: NamePath;
  min?: number;
}

const UnitAssistInput: React.FC<UnitAssistInputProps> = ({
  form,
  disabled = false,
  fullNamePath,
  localNamePath,
  required,
  readOnly,
  debounce,
  currency,
  currencyNamePath,
  $itemSize,
  max,
  min,
  precision,
}: UnitAssistInputProps) => {
  const [valueReadOnly, setValueReadOnly] = useState(readOnly);
  const [percentReadOnly, setPercentReadOnly] = useState(readOnly);
  const theme = useTheme();

  const { getFieldValue, setFields } = form;
  const lineLevelPath = [...fullNamePath.slice(0, lineLevelNamePathDepth)];
  const percentAssistFullNamePath = [
    ...fullNamePath.slice(0, -1),
    'unitAssistPercentage',
  ];
  const percentAssistLocalNamePath = [
    ...localNamePath.slice(0, -1),
    'unitAssistPercentage',
  ];

  const lineLevelUnitValue =
    Number(
      getFieldValue([...fullNamePath.slice(0, -1), 'unitValue', 'value']),
    ) || 0;

  // Set the line-level assist (applicable if we're on the entry page)
  const handleSetUnitAssist = (val = 0) => {
    setPercentReadOnly(true);
    const lineLevelQuantity = getFieldValue([...lineLevelPath, 'quantity']);

    if (lineLevelQuantity) {
      const lineLevelAssist = val * lineLevelQuantity;
      const lineLevelAssistPath = [...lineLevelPath, 'totalAssist', 'value'];
      const fieldToUpdate = [
        {
          name: lineLevelAssistPath,
          value: lineLevelAssist,
        },
      ];
      fieldToUpdate.push({
        name: percentAssistFullNamePath,
        value: (val / lineLevelUnitValue) * 100,
      });

      setFields(fieldToUpdate);
    }
  };

  const handleSetPercentAssist = (val = 0) => {
    setValueReadOnly(true);
    const lineLevelQuantity = getFieldValue([...lineLevelPath, 'quantity']);
    const unitAssistValuePath = [...fullNamePath, 'value'];

    if (lineLevelQuantity) {
      const unitAssitValue = (val / 100) * lineLevelUnitValue;
      const lineLevelAssist = unitAssitValue * lineLevelQuantity;
      const lineLevelAssistPath = [...lineLevelPath, 'totalAssist', 'value'];
      setFields([
        {
          name: lineLevelAssistPath,
          value: lineLevelAssist,
        },
        {
          name: unitAssistValuePath,
          value: unitAssitValue,
        },
      ]);
    }
  };
  // Remove assist at the unit level and at the line-level (if we're on the entry page)
  const removeAssist = () => {
    setFields([
      {
        name: [...fullNamePath],
        value: undefined,
      },
      {
        name: [...lineLevelPath, 'totalAssist'],
        value: undefined,
      },
      {
        name: percentAssistFullNamePath,
        value: undefined,
      },
    ]);
  };

  const handlePercentClick = () => {
    setValueReadOnly(true);
  };
  const handleValueClick = () => {
    setPercentReadOnly(true);
  };
  const handlePercentBlur = () => {
    setValueReadOnly(false);
  };

  const handleValueBlur = () => {
    setPercentReadOnly(false);
  };

  return (
    <>
      <p>
        Enter assist percentage or assist Value to calculate the total assist
        value
      </p>
      <StyledUnitAssistInputContainer>
        <CurrencyField
          form={form}
          currency={currency}
          currencyNamePath={currencyNamePath}
          debounce={debounce}
          label={<LabelAbove text="Unit Assist" />}
          fullNamePath={fullNamePath}
          localNamePath={localNamePath}
          $itemSize={$itemSize || theme.size.tiny_short}
          precision={precision}
          required={required}
          disabled={disabled}
          max={max}
          readOnly={readOnly || valueReadOnly}
          min={min}
          onChange={handleSetUnitAssist}
          onBlur={handleValueBlur}
          onClick={handleValueClick}
        />
        <PercentInput
          localNamePath={percentAssistLocalNamePath}
          debounce={debounce}
          label={<LabelAbove text="Unit Assist Percent" />}
          $itemSize={$itemSize || theme.size.tiny_short}
          disabled={disabled}
          precision={precision}
          readOnly={readOnly || percentReadOnly}
          onClick={handlePercentClick}
          onBlur={handlePercentBlur}
          onChange={handleSetPercentAssist}
        />
        {!readOnly && (
          <StyledUnitAssistInputMinusButtonWrapperDiv>
            <MinusButton
              label="unit assist"
              firstRow={true}
              onRemove={removeAssist}
              className="container-minus"
              disabled={disabled}
            />
          </StyledUnitAssistInputMinusButtonWrapperDiv>
        )}
      </StyledUnitAssistInputContainer>
    </>
  );
};

export default UnitAssistInput;
